// deps
import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { useCalendarCell } from "@react-aria/calendar";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useRef } from "react";
import { FormattedMessage } from "react-intl";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * @param {dayjs.Dayjs} targetDate
 * @param {string} time
 * @returns {dayjs.Dayjs}
 */
function setTime(targetDate, time) {
  const [hour, minute, second] = time.split(":").map(Number);
  return targetDate
    .set("hour", hour)
    .set("minute", minute)
    .set("second", second);
}

/**
 * @param {object} date
 * @param {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]} unavailableRanges
 * @param {string} opening_hour
 * @param {string} closing_hour
 * @returns {boolean}
 */
function isDateFullyUnavailable(
  date,
  unavailableRanges,
  opening_hour,
  closing_hour,
) {
  const targetDate = dayjs.utc(date);
  const dayStart = setTime(targetDate, opening_hour);
  const dayEnd = setTime(targetDate, closing_hour);

  let mergedRanges = [];

  unavailableRanges
    .map(({ from, to }) => ({ from: dayjs.utc(from), to: dayjs.utc(to) }))
    .filter(({ from, to }) => from.isBefore(dayEnd) && to.isAfter(dayStart))
    .sort((a, b) => a.from.valueOf() - b.from.valueOf())
    .forEach(({ from, to }) => {
      if (mergedRanges.length === 0) {
        mergedRanges.push({ from, to });
      } else {
        const lastRange = mergedRanges[mergedRanges.length - 1];
        if (
          lastRange.to.isSameOrAfter(from) ||
          lastRange.to.add(1, "minute").isSameOrAfter(from)
        ) {
          // Fusionner les plages si elles se chevauchent ou se touchent
          lastRange.to = lastRange.to.isAfter(to) ? lastRange.to : to;
        } else {
          // Ajouter une nouvelle plage
          mergedRanges.push({ from, to });
        }
      }
    });

  // Vérifier si toute la plage de disponibilité est couverte sans trou
  let isFullyUnavailable = false;
  if (mergedRanges.length > 0) {
    let currentStart = dayStart;
    isFullyUnavailable = mergedRanges.every(({ from, to }) => {
      if (from.isAfter(currentStart)) {
        return false;
      }
      currentStart = to;
      return true;
    });

    // Vérifier si la dernière plage couvre bien la fin de journée
    if (currentStart.isBefore(dayEnd)) {
      isFullyUnavailable = false;
    }
  }

  return isFullyUnavailable;
}

/**
 * @param {import("@raiden/library/types/common/DatePicker").UnavailableRanges} range
 * @param {object} date
 * @param {boolean} isMobile
 * @returns {string | JSX.Element}
 */
function formatRangeDisplay(range, date, isMobile) {
  const fromParis = dayjs(range.from).tz("Europe/Paris");
  const toParis = dayjs(range.to).tz("Europe/Paris");
  const isSameDay = fromParis.isSame(toParis, "day");

  /**
   * @param {import("dayjs").Dayjs} time
   * @returns {string}
   */
  function adjustDateWithTime(time) {
    const minutes = time.minute();
    return minutes > 0 ? time.format("H[h]mm") : time.format("H[h]");
  }

  // Format the date to display the time if reserved for the same day
  if (isSameDay) {
    return isMobile
      ? [fromParis, toParis].map((t) => t.format("H")).join("-")
      : [fromParis, toParis].map(adjustDateWithTime).join(" - ");
  }

  // Format the date to display the time if reserved for more than one day
  if (fromParis.isSame(date, "day")) {
    return isMobile ? (
      <FormattedMessage
        defaultMessage="Pr {time}"
        values={{ time: adjustDateWithTime(fromParis) }}
      />
    ) : (
      <FormattedMessage
        defaultMessage="Pris à {time}"
        values={{ time: adjustDateWithTime(fromParis) }}
      />
    );
  }
  return isMobile ? (
    <FormattedMessage
      defaultMessage="Lib {time}"
      values={{ time: adjustDateWithTime(toParis) }}
    />
  ) : (
    <FormattedMessage
      defaultMessage="Libre à {time}"
      values={{ time: adjustDateWithTime(toParis) }}
    />
  );
}

/**
 * @typedef Props
 * @property {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]} unavailableRanges
 * @property {import("@react-stately/calendar").CalendarState} state
 * @property {object} date
 * @property {boolean} isBookableOnline
 * @property {string | null} opening_hour
 * @property {string | null} closing_hour
 * @property {() => void} [onClick]
 */
/**
 * @param {Props} props
 */
export function CalendarCell({
  unavailableRanges,
  state,
  date,
  isBookableOnline,
  opening_hour,
  closing_hour,
  onClick,
}) {
  let ref = useRef(null);
  let { cellProps, buttonProps, isUnavailable, formattedDate, isDisabled } =
    useCalendarCell({ date }, state, ref);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const isFullyUnavailable = isDateFullyUnavailable(
    date,
    unavailableRanges,
    opening_hour || "00:00:00",
    closing_hour || "23:59:59",
  );

  const dateIsPast = dayjs(date).isBefore(dayjs(), "day");

  return (
    <Box
      as="td"
      h={{ base: "80px", md: "115px" }}
      w={{ base: "50px", md: "110px" }}
      p="0"
      {...cellProps}
      border="1px solid"
      borderColor="gray.200">
      <Button
        p="0"
        h="full"
        width="full"
        ref={ref}
        {...buttonProps}
        onClick={(event) => {
          onClick?.();
          buttonProps.onClick?.(event);
        }}
        isDisabled={
          dateIsPast || isDisabled || isFullyUnavailable || !isBookableOnline
        }
        bg={dateIsPast ? "gray.100" : "gray.50"}
        borderRadius="0"
        _hover={{
          bg: dateIsPast
            ? "gray.100"
            : isBookableOnline
            ? "brandPrimary.100"
            : "none",
          cursor:
            dateIsPast || isDisabled || isFullyUnavailable
              ? "not-allowed"
              : !isBookableOnline
              ? "default"
              : "pointer",
        }}
        style={{
          opacity: isDisabled ? 0.5 : 1,
        }}>
        <VStack w="full" h="full" alignItems="flex-start" spacing="0">
          <Text
            w="full"
            p=".25rem .625rem"
            fontSize=".80rem"
            fontWeight={500}
            color={isUnavailable ? "gray.800" : "brandPrimary.700"}
            align="right">
            {formattedDate}
          </Text>

          <VStack
            display="flex"
            w="full"
            h="full"
            px=".25rem"
            spacing=".125rem"
            overflow="hidden"
            overflowY="auto">
            {isFullyUnavailable ? (
              <Badge
                paddingY=".2rem"
                width="full"
                variant="solid"
                borderRadius=".25rem"
                colorScheme="brandPrimary"
                fontSize={{ base: ".50rem", md: ".60rem" }}>
                <HStack>
                  <Text fontSize="inherit" color="inherit" fontWeight="inherit">
                    {isMobile ? (
                      <>
                        <FormattedMessage defaultMessage="Comp" />
                      </>
                    ) : (
                      <FormattedMessage defaultMessage="Complet" />
                    )}
                  </Text>
                </HStack>
              </Badge>
            ) : (
              <>
                {unavailableRanges
                  .filter((range) =>
                    dayjs(date).isBetween(
                      dayjs(range.from),
                      dayjs(range.to),
                      "day",
                      "[]",
                    ),
                  )
                  .map((range, index) => (
                    <Badge
                      key={index}
                      paddingY=".2rem"
                      width="full"
                      variant="solid"
                      borderRadius=".25rem"
                      colorScheme="brandPrimary"
                      fontSize={{ base: ".55rem", md: ".65rem" }}>
                      <HStack>
                        <Text
                          fontSize="inherit"
                          color="inherit"
                          fontWeight="inherit">
                          {formatRangeDisplay(range, date, isMobile ?? false)}
                        </Text>
                      </HStack>
                    </Badge>
                  ))}
              </>
            )}
          </VStack>
        </VStack>
      </Button>
    </Box>
  );
}
